import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MapPin, Trophy } from 'lucide-react';
import { Button } from '../ui/button';
import { useNiceWeather } from '../../hooks/useNiceWeather';
import { HeroWeatherLayer } from './HeroWeatherLayer';

export const Hero = () => {
  const { t } = useTranslation();
  const weather = useNiceWeather();
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0 hero-parallax"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1643914729809-4aa59fdc4c17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxOaWNlJTIwRnJhbmNlJTIwcHJvbWVuYWRlJTIwZGVzJTIwYW5nbGFpcyUyMHN1bm55fGVufDB8fHx8MTc3MDU0MTU0N3ww&ixlib=rb-4.1.0&q=85')`,
        }}
      />
      <HeroWeatherLayer weather={weather} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
          data-testid="hero-bac-banner"
          className="inline-flex items-center gap-2 mb-6 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-amber-300/40 text-amber-100"
        >
          <Trophy className="w-3.5 h-3.5 flex-shrink-0 text-amber-300" />
          <span className="font-inter font-medium text-[11px] sm:text-xs tracking-wide">
            {t('hero.bacBanner')}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center justify-center gap-2 mb-6"
        >
          <MapPin className="w-4 h-4 text-amber-400" />
          <span className="text-white/80 text-sm font-inter tracking-wider uppercase">
            {t('hero.location')}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-playfair text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight"
        >
          {t('hero.title1')} <span className="italic text-amber-400">{t('hero.title1Italic')}</span>,
          <br />
          {t('hero.title2')} <span className="italic text-amber-400">{t('hero.title2Italic')}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          data-testid="hero-year-tagline"
          className="font-caveat text-2xl sm:text-3xl lg:text-4xl text-amber-300 mb-8 leading-tight"
        >
          <span aria-hidden="true" className="mr-1">✨</span>
          {t('hero.yearTagline')}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="font-inter text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10"
        >
          {t('hero.subtitle')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            data-testid="hero-apply-btn"
            onClick={() => scrollToSection('#contact')}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full px-10 py-6 text-lg btn-shine shadow-xl hover:shadow-2xl transition-all"
          >
            {t('hero.ctaPrimary')}
          </Button>
          <Button
            data-testid="hero-discover-btn"
            onClick={() => scrollToSection('#about')}
            variant="outline"
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-full px-10 py-6 text-lg backdrop-blur-sm"
          >
            {t('hero.ctaSecondary')}
          </Button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: [0.95, 1.05, 0.95],
            textShadow: [
              '0 0 10px rgba(255,140,0,0.6)',
              '0 0 24px rgba(255,120,0,0.95), 0 0 48px rgba(255,80,0,0.5)',
              '0 0 10px rgba(255,140,0,0.6)',
            ],
          }}
          transition={{
            opacity: { duration: 0.8, delay: 1 },
            scale: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
            textShadow: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
          }}
          data-testid="hero-halloween"
          className="font-caveat text-3xl sm:text-4xl lg:text-5xl mt-8"
          style={{ color: '#ff7a1a' }}
        >
          <span aria-hidden="true" className="mr-2">🎃</span>
          Happy Halloween
          <span aria-hidden="true" className="ml-2">🦇</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-20 grid grid-cols-3 gap-8 max-w-3xl mx-auto"
        >
          {[
            { id: 'languages', value: '4', label: t('hero.stat1Label') },
            { id: 'nationalities', value: '20+', label: t('hero.stat2Label') },
            { id: 'success', value: '98%', label: t('hero.stat3Label') },
          ].map((stat) => (
            <div key={stat.id} className="text-center">
              <div className="font-playfair text-3xl sm:text-4xl font-bold text-amber-400">
                {stat.value}
              </div>
              <div className="text-white/60 text-sm font-inter mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.5 }, y: { repeat: Infinity, duration: 2 } }}
        onClick={() => scrollToSection('#about')}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors"
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
};
